import React, { useState, useEffect } from 'react';

interface RenameChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newTitle: string) => void;
  currentTitle: string;
  isRenaming?: boolean;
}

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * 旧 `@keyframes slideIn`（opacity 0 / translateY(-20px) → 1 / 0）は
 * globals.css の既存 `em-slide-in` と同内容なのでそれを再利用する。 */
const MODAL_BACKDROP =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center ' +
  'z-[10000] backdrop-blur-[2px]';

const MODAL_CONTENT =
  'bg-white rounded-tasky-md w-[90%] max-w-[480px] ' +
  'shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] ' +
  'animate-[em-slide-in_0.2s_ease-out]';

const MODAL_HEADER = 'p-6 border-b border-b-gray-200'; /* #E5E7EB */

const MODAL_TITLE = 'text-[1.25rem] font-semibold text-gray-900 m-0'; /* #111827 */

const MODAL_BODY = 'p-6';

const INPUT_WRAPPER = 'relative';

const INPUT =
  'w-full px-4 py-3 border border-gray-300 rounded-tasky-xs text-[0.9375rem] text-gray-900 ' +
  'transition-[border-color] duration-200 ' +
  'focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] ' +
  'disabled:bg-gray-50 disabled:cursor-not-allowed'; /* #D1D5DB / #3B82F6 / #F9FAFB */

const CHAR_COUNT_BASE =
  'absolute right-3 top-1/2 -translate-y-1/2 text-[0.75rem] pointer-events-none';
const CHAR_COUNT_ERROR = 'text-red-600'; /* #DC2626 */
const CHAR_COUNT_NORMAL = 'text-gray-400'; /* #9CA3AF */

const ERROR_MESSAGE =
  'mt-2 p-2 bg-red-50 border-l-[3px] border-l-red-600 rounded text-[0.875rem] text-red-600'; /* #FEF2F2 / #DC2626 */

const MODAL_FOOTER = 'px-6 py-4 border-t border-t-gray-200 flex justify-end gap-3';

const BUTTON_BASE =
  'px-5 py-2.5 rounded-tasky-xs text-[0.875rem] font-medium cursor-pointer transition-all duration-200';
const BUTTON_PRIMARY =
  'border-0 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed'; /* #3B82F6 / #2563EB / #93C5FD */
const BUTTON_DEFAULT =
  'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'; /* #374151 / #D1D5DB / #F9FAFB */

const RenameChatModal: React.FC<RenameChatModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentTitle,
  isRenaming = false,
}) => {
  const [newTitle, setNewTitle] = useState(currentTitle);
  const [error, setError] = useState('');

  // モーダルが開かれた時に現在のタイトルをセット
  useEffect(() => {
    if (isOpen) {
      setNewTitle(currentTitle);
      setError('');
    }
  }, [isOpen, currentTitle]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = () => {
    const trimmedTitle = newTitle.trim();

    // バリデーション
    if (trimmedTitle.length === 0) {
      setError('タイトルを入力してください');
      return;
    }

    if (trimmedTitle.length > 255) {
      setError('タイトルは255文字以内で入力してください');
      return;
    }

    // 変更がない場合は閉じる
    if (trimmedTitle === currentTitle.trim()) {
      onClose();
      return;
    }

    onConfirm(trimmedTitle);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={MODAL_BACKDROP} onClick={onClose}>
      <div className={MODAL_CONTENT} onClick={(e) => e.stopPropagation()}>
        <div className={MODAL_HEADER}>
          <h2 className={MODAL_TITLE}>チャットタイトルの変更</h2>
        </div>

        <div className={MODAL_BODY}>
          <div className={INPUT_WRAPPER}>
            <input
              className={INPUT}
              type="text"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="新しいタイトルを入力"
              autoFocus
              disabled={isRenaming}
              maxLength={255}
            />
            <div
              className={`${CHAR_COUNT_BASE} ${newTitle.length > 255 ? CHAR_COUNT_ERROR : CHAR_COUNT_NORMAL}`}
            >
              {newTitle.length} / 255
            </div>
          </div>

          {error && <div className={ERROR_MESSAGE}>{error}</div>}
        </div>

        <div className={MODAL_FOOTER}>
          <button className={`${BUTTON_BASE} ${BUTTON_DEFAULT}`} onClick={onClose} disabled={isRenaming}>
            キャンセル
          </button>
          <button className={`${BUTTON_BASE} ${BUTTON_PRIMARY}`} onClick={handleSubmit} disabled={isRenaming}>
            {isRenaming ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenameChatModal;
